import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Building2 } from "lucide-react";
import { professionnels } from "../mock/servicesData";
import QuoteForm from "../components/QuoteForm";
import Reveal from "../components/Reveal";

const Professionnels = () => {
  const navigate = useNavigate();
  useEffect(() => window.scrollTo(0, 0), []);

  return (
    <div className="pt-16 sm:pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={professionnels.hero.image} alt="Déménagement professionnel" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#131f38]/95 to-[#1B2A4A]/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-28">
          <div className="max-w-2xl animate-fadeUp">
            <span className="inline-block bg-[#C9A227] text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-5 uppercase tracking-wider">Professionnels</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight">{professionnels.hero.title}</h1>
            <p className="mt-5 text-lg text-gray-200 max-w-xl">{professionnels.hero.subtitle}</p>
            <button onClick={() => navigate("/contact")} className="mt-8 inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#b8931f] text-white font-semibold px-7 py-3.5 rounded-lg transition-colors shadow-lg">
              Demander une étude personnalisée <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-[#1B2A4A] mb-5">{professionnels.intro.title}</h2>
            {professionnels.intro.paragraphs.map((p, i) => (
              <p key={i} className="text-gray-600 leading-relaxed mb-4">{p}</p>
            ))}
          </div>
          <img src={professionnels.intro.image} alt="Bureau professionnel" className="rounded-2xl shadow-xl w-full h-[400px] object-cover" />
        </div>
      </section>

      {/* PRESTATIONS */}
      <section className="py-16 bg-[#f7f9fc]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-[#C9A227] font-semibold text-sm uppercase tracking-wider">Prestations</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mt-2">Nos prestations professionnelles</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {professionnels.prestations.map((p, i) => {
              const Icon = Icons[p.icon] || Building2;
              return (
                <Reveal key={i} delay={(i % 3) * 100} className="group bg-white rounded-2xl p-7 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-[#C9A227]/40 transition-all duration-300">
                  <div className="w-14 h-14 rounded-xl bg-[#1B2A4A]/5 group-hover:bg-[#1B2A4A] flex items-center justify-center transition-colors duration-300">
                    <Icon size={26} className="text-[#1B2A4A] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-[#1B2A4A]">{p.title}</h3>
                  <p className="mt-2 text-gray-600 text-sm leading-relaxed">{p.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* METHOD */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-[#C9A227] font-semibold text-sm uppercase tracking-wider">Méthode</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mt-2">Notre méthode</h2>
          </div>
          <div className="space-y-6">
            {professionnels.method.map((m, idx) => (
              <Reveal key={m.num} delay={idx * 80} className="flex gap-5 bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-full bg-[#C9A227] text-white flex items-center justify-center text-lg font-bold shrink-0">
                  {m.num}
                </div>
                <div>
                  <h3 className="font-bold text-[#1B2A4A] text-lg">{m.title}</h3>
                  <p className="text-gray-600 mt-1">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section className="py-16 bg-[#1B2A4A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-[#C9A227] font-semibold text-sm uppercase tracking-wider">Secteurs</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-2 mb-3">Secteurs d'activité</h2>
            <p className="text-gray-300">Nous intervenons pour tous types d'entreprises et de secteurs.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {professionnels.sectors.map((s, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-center text-white font-medium hover:bg-white/10 transition-colors">
                {s}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENGAGEMENTS */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-[#C9A227] font-semibold text-sm uppercase tracking-wider">Engagements</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mt-2">Nos engagements</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {professionnels.engagements.map((e, i) => {
              const Icon = Icons[e.icon] || Icons.ShieldCheck;
              return (
                <Reveal key={i} delay={i * 120} className="text-center bg-white rounded-2xl p-8 border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <div className="mx-auto w-16 h-16 rounded-full bg-[#1B2A4A]/5 flex items-center justify-center">
                    <Icon size={28} className="text-[#C9A227]" />
                  </div>
                  <h3 className="mt-5 font-bold text-[#1B2A4A] text-lg">{e.title}</h3>
                  <p className="mt-2 text-gray-600 text-sm">{e.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="py-20 bg-[#f7f9fc]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mb-3">Demandez une étude personnalisée</h2>
            <p className="text-gray-600">Contactez-nous pour un audit gratuit de vos besoins et un devis sur mesure.</p>
          </div>
          <QuoteForm />
        </div>
      </section>
    </div>
  );
};

export default Professionnels;
